import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { Artefact } from "@/lib/artefacts";
import { types, useServices } from "@/lib/openapi";
import { followLink } from "@/lib/openapi/helpers";
import {
    AuthStatus,
    useAuthenticationManager,
} from "@/stores/authentication-store";

export type Stek = types.Stek;

export const useSteksStore = defineStore("steks", () => {
    const toast = useToast();
    const isLoading = ref(false);
    const steks = ref<Stek[]>();
    const { organization } = useOrganizationManager();
    const { authStatus } = useAuthenticationManager();
    const api = useServices({
        requires: {
            auth: true,
            organization: true,
            stek: false,
        },
    });

    const controller = ref(new AbortController());

    const fetchSteks = async () => {
        if (
            authStatus.value !== AuthStatus.SIGNED_IN ||
            !organization.value?.identity
        ) {
            return;
        }

        controller.value.abort();
        controller.value = new AbortController();

        isLoading.value = true;
        try {
            const { response, data } =
                await api.startStekStekMgtGetSteksByOrganizationStartPost({
                    body: undefined,
                    signal: controller.value.signal,
                });

            if (response.ok) {
                steks.value = data?.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
            } else {
                if (response.status === 0) {
                    // Request was aborted by the AbortController
                    return;
                }
                throw new Error(
                    `Request failed with status ${response.status}`
                );
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                return;
            } else {
                throw e;
            }
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Fetch the logo for a given Stek.
     */
    const fetchLogo = async (stek: Stek): Promise<Artefact | undefined> => {
        if (!stek.logo) {
            return;
        }

        const { response, data } = await followLink<File>(
            stek.logo,
            "get",
            undefined,
            undefined,
            undefined,
            {
                requires: {
                    auth: true,
                    organization: true,
                    stek: true,
                },
                overrides: {
                    stekId: stek.identity,
                },
            }
        );

        if (!response.ok) {
            if (response.status === 404) {
                // Not found is ok, we'll use the placeholder
                return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return new Artefact({
            name: "stekLogo",
            identity: stek.logo.identity,
            blob: data!,
            mimetype: data!.type,
            content: await data!.text(),
        });
    };

    // Cannot get steks if we aren't signed in.
    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus === AuthStatus.SIGNED_IN) {
                fetchSteks();
            }
        },
        { immediate: true }
    );

    watch(
        () => organization.value?.identity,
        async (identity, oldIdentity) => {
            if (identity) {
                await fetchSteks();
            } else {
                if (oldIdentity) {
                    steks.value = undefined;
                }
            }
        },
        // The second watcher triggers whenever the organization's identity changes.
        // If there is an identity, it fetches the steks for that organization.
        // If the identity becomes undefined (e.g., user switches orgs or logs out), it clears steks (if there was an org before).
        // `immediate: true` ensures the effect runs once right away on watcher setup.
        { immediate: true }
    );

    /**
     * Create a new Stek.
     */
    const create = async (
        name: string,
        fqdn?: string,
        logo?: File,
        opts: { showToast: boolean } = { showToast: true }
    ) => {
        isLoading.value = true;
        const { response } = await api.startStekStekMgtCreateStekStartPost({
            body: { name, fqdn, logo },
        });

        if (response.ok) {
            await fetchSteks();
        } else if (opts.showToast) {
            toast.add({
                severity: "error",
                summary: "Could not create stek",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
    };

    return {
        create,
        fetchSteks,
        steks,
        isLoading,
        fetchLogo,
    };
});

export const useSteksManager = () => storeToRefs(useSteksStore());

/**
 * Helper composable to provide steks with logos synchronously (but filled async).
 */
export function useSteksWithLogo() {
    const { fetchLogo } = useSteksStore();
    const { steks } = useSteksManager();
    const logos = ref<Map<string, string>>(new Map());
    const loading = ref(false);

    const steksWithLogos = computed(
        () =>
            steks.value?.map((stek) => ({
                stek,
                logoDataUrl: logos.value.get(stek.identity),
            })) || []
    );

    watch(steks, async () => {
        if (!steks.value?.length) return;

        loading.value = true;

        steks.value.map(async (stek) => {
            if (!logos.value.has(stek.identity)) {
                const logo = await fetchLogo(stek);
                const dataUrl = await logo?.getDataUrl();
                if (dataUrl) {
                    logos.value.set(stek.identity, dataUrl);
                }
            }
        });

        loading.value = false;
    });

    return { steksWithLogos, loading };
}
